import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { db } from '../../services/firebase';
import { collection, getDocs, query, orderBy, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import { generateEstimatePDF, uploadPDFToStorage } from '../../utils/pdfGenerator';
import { createSignatureRequest, checkSignatureStatus } from '../../services/signatureService';
import { translateAndCorrect, spanishToEnglish, englishToSpanish } from '../../utils/translationService';

const AdminEstimates = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { createEstimateNotification } = useNotifications();
  const [estimates, setEstimates] = useState([]);
  const [filteredEstimates, setFilteredEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messagesListener, setMessagesListener] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  const [enhancingText, setEnhancingText] = useState(false);
  const [activeTab, setActiveTab] = useState('ALL');
  const [globalListener, setGlobalListener] = useState(null);
  
  // E-signature states
  const [sendingSignature, setSendingSignature] = useState(false);
  const [checkingSignature, setCheckingSignature] = useState(false);

  // Inline confirmation states
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    quoted: 0,
    completed: 0
  });

  useEffect(() => {
    loadEstimates();
    setupGlobalListener();
    
    return () => {
      if (globalListener) {
        globalListener();
      }
      if (messagesListener) {
        messagesListener();
      }
    };
  }, []);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    calculateStats();
    filterEstimates();
  }, [estimates, activeTab]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Function to check and fix estimates with missing file references
  const checkAndFixEstimateFiles = async (estimate) => {
    if (!estimate.id || !estimate.userId) return estimate;
    
    try {
      // Check if estimate already has files
      if (estimate.files && estimate.files.length > 0) {
        console.log(`Estimate ${estimate.id} already has ${estimate.files.length} files`);
        return estimate;
      }
      
      // Check Firebase Storage for files
      const storageRef = ref(storage, `estimates/${estimate.userId}/${estimate.id}`);
      const listResult = await listAll(storageRef);
      
      if (listResult.items.length > 0) {
        console.log(`Found ${listResult.items.length} orphaned files for estimate ${estimate.id}`);
        
        // Get file details and download URLs
        const filePromises = listResult.items.map(async (item) => {
          try {
            const downloadURL = await getDownloadURL(item);
            const fileName = item.name;
            
            // Try to determine file type from name
            const fileExtension = fileName.split('.').pop().toLowerCase();
            let fileType = 'application/octet-stream';
            if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
              fileType = `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`;
            } else if (['mp4', 'webm', 'avi', 'mov'].includes(fileExtension)) {
              fileType = `video/${fileExtension}`;
            }
            
            return {
              name: fileName,
              url: downloadURL,
              size: 0, // We can't get size from storage reference easily
              type: fileType,
              storagePath: item.fullPath
            };
          } catch (error) {
            console.error('Error getting file details:', error);
            return null;
          }
        });
        
        const files = (await Promise.all(filePromises)).filter(f => f !== null);
        
        if (files.length > 0) {
          console.log('Updating estimate with recovered files:', files);
          
          // Update Firestore document
          await updateDoc(doc(db, 'estimates', estimate.id), {
            files: files,
            updatedAt: new Date()
          });
          
          // Return updated estimate
          return {
            ...estimate,
            files: files
          };
        }
      }
      
      return estimate;
    } catch (error) {
      console.error('Error checking/fixing estimate files:', error);
      return estimate;
    }
  };

  const setupGlobalListener = () => {
    const estimatesRef = collection(db, 'estimates');
    const unsubscribe = onSnapshot(estimatesRef, (snapshot) => {
      const estimatesList = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        estimatesList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          submittedDate: data.createdAt?.toDate ? 
            data.createdAt.toDate().toISOString().split('T')[0] : 
            (data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
        });
      });
      
      estimatesList.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setEstimates(estimatesList);
      setLoading(false);
    });
    
    setGlobalListener(() => unsubscribe);
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
  };

  const loadEstimates = async () => {
    setLoading(true);
    try {
      const estimatesRef = collection(db, 'estimates');
      let querySnapshot;
      
      try {
        const q = query(estimatesRef, orderBy('createdAt', 'desc'));
        querySnapshot = await getDocs(q);
      } catch (orderError) {
        querySnapshot = await getDocs(estimatesRef);
      }
      
      const estimatesList = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        estimatesList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          submittedDate: data.createdAt?.toDate ? 
            data.createdAt.toDate().toISOString().split('T')[0] : 
            (data.createdAt ? new Date(data.createdAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0])
        });
      });
      
      estimatesList.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setEstimates(estimatesList);
      
    } catch (error) {
      console.error('Error loading estimates:', error);
      showNotification('Failed to load estimates. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = () => {
    const total = estimates.length;
    const pending = estimates.filter(est => est.status === 'pending').length;
    const inProgress = estimates.filter(est => est.status === 'in-progress').length;
    const quoted = estimates.filter(est => est.status === 'quoted').length;
    const completed = estimates.filter(est => est.status === 'completed' || est.status === 'approved').length;
    
    setStats({ total, pending, inProgress, quoted, completed });
  };

  const filterEstimates = () => {
    let filtered = estimates;
    
    switch (activeTab) {
      case 'PENDING':
        filtered = estimates.filter(est => est.status === 'pending');
        break;
      case 'IN PROGRESS':
        filtered = estimates.filter(est => est.status === 'in-progress');
        break;
      case 'QUOTED':
        filtered = estimates.filter(est => est.status === 'quoted');
        break;
      case 'COMPLETED':
        filtered = estimates.filter(est => est.status === 'completed' || est.status === 'approved');
        break;
      default:
        filtered = estimates;
    }
    
    setFilteredEstimates(filtered);
  };

  const setupMessagesListener = (estimateId) => {
    if (messagesListener) {
      messagesListener();
    }
    
    const estimateRef = doc(db, 'estimates', estimateId);
    const unsubscribe = onSnapshot(estimateRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setSelectedEstimate(prev => ({
          ...prev,
          ...data,
          id: doc.id
        }));
      }
    });
    
    setMessagesListener(() => unsubscribe);
  };

  const handleViewEstimate = async (estimate) => {
    console.log('Admin viewing estimate:', estimate);
    console.log('Estimate files:', estimate.files);
    
    // Try to recover orphaned files if no files are present
    const updatedEstimate = await checkAndFixEstimateFiles(estimate);
    
    setSelectedEstimate(updatedEstimate);
    setStatusUpdate(updatedEstimate.status || 'pending');
    setQuoteAmount(updatedEstimate.quoteAmount || '');
    setQuoteNotes(updatedEstimate.quoteNotes || '');
    setStatusUpdateSuccess(false); // Reset success state
    setUpdatingStatus(false); // Reset updating state
    setShowModal(true);
    setupMessagesListener(updatedEstimate.id);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEstimate(null);
    setNewMessage('');
    setStatusUpdate('');
    setQuoteAmount('');
    setQuoteNotes('');
    
    if (messagesListener) {
      messagesListener();
      setMessagesListener(null);
    }
  };

  const enhanceTextWithAI = async () => {
    if (!newMessage.trim()) {
      showNotification('Please enter some text to enhance.', 'error');
      return;
    }

    setEnhancingText(true);
    try {
      // Translate and correct grammar using free services
      // 1. Detects language (Spanish/English)
      // 2. Translates to English if needed (LibreTranslate/MyMemory/Google)
      // 3. Corrects grammar (LanguageTool)
      const result = await translateAndCorrect(newMessage.trim());
      
      if (result.success) {
        setNewMessage(result.processedText);
        
        // Build notification message based on steps performed
        const stepsPerformed = result.steps.map(s => s.step).join(' → ');
        const details = result.steps
          .map(s => {
            if (s.step === 'translate') return `translated via ${s.service}`;
            if (s.step === 'grammar') return `${s.corrections} corrections`;
            return s.step;
          })
          .join(', ');
        
        showNotification(`Text enhanced! (${details})`, 'success');
      } else {
        throw new Error(result.error || 'Enhancement failed');
      }
      
    } catch (error) {
      console.error('Error enhancing text:', error);
      showNotification('Failed to enhance text. Please try again.', 'error');
    } finally {
      setEnhancingText(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedEstimate || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      const message = {
        id: Date.now().toString(),
        text: newMessage.trim(),
        sender: 'admin',
        senderName: currentUser.displayName || 'Admin',
        senderEmail: currentUser.email,
        timestamp: new Date(),
        read: false
      };
      
      const currentMessages = selectedEstimate.messages || [];
      const updatedMessages = [...currentMessages, message];
      
      await updateDoc(doc(db, 'estimates', selectedEstimate.id), {
        messages: updatedMessages,
        updatedAt: new Date(),
        hasUnreadMessages: false
      });
      
      setNewMessage('');
      showNotification('Message sent successfully!', 'success');
      
    } catch (error) {
      console.error('Error sending message:', error);
      showNotification('Failed to send message. Please try again.', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  const sendQuote = async () => {
    if (!quoteAmount || !newMessage.trim()) {
      showNotification('Please enter both quote amount and message.', 'error');
      return;
    }

    setSendingMessage(true);
    try {
      const message = {
        id: Date.now().toString(),
        text: newMessage.trim(),
        sender: 'admin',
        senderName: currentUser.displayName || 'Admin',
        senderEmail: currentUser.email,
        timestamp: new Date(),
        read: false,
        isQuote: true,
        quoteAmount: parseFloat(quoteAmount),
        quoteNotes: quoteNotes.trim()
      };
      
      const currentMessages = selectedEstimate.messages || [];
      const updatedMessages = [...currentMessages, message];
      
      await updateDoc(doc(db, 'estimates', selectedEstimate.id), {
        messages: updatedMessages,
        status: 'quoted',
        quoteAmount: parseFloat(quoteAmount),
        quoteNotes: quoteNotes.trim(),
        updatedAt: new Date(),
        hasUnreadMessages: false
      });
      
      // Notify client about quote
      try {
        const updatedEstimate = {
          ...selectedEstimate,
          quoteAmount: parseFloat(quoteAmount),
          status: 'quoted'
        };
        await createEstimateNotification(updatedEstimate, 'estimate_quoted', selectedEstimate.userId);
      } catch (notificationError) {
        console.error('Error sending quote notification to client:', notificationError);
      }

      setNewMessage('');
      setStatusUpdate('quoted');
      showNotification('Quote sent successfully!', 'success');
      
    } catch (error) {
      console.error('Error sending quote:', error);
      showNotification('Failed to send quote. Please try again.', 'error');
    } finally {
      setSendingMessage(false);
    }
  };

  // E-signature functionality
  const sendForSignature = async () => {
    if (!selectedEstimate || !selectedEstimate.quoteAmount) {
      showNotification('Please send a quote first before requesting signature.', 'error');
      return;
    }

    setSendingSignature(true);
    
    try {
      console.log('Starting signature request process...');
      console.log('Current user:', currentUser);
      console.log('Selected estimate:', selectedEstimate);
      
      // Generate PDF for the estimate
      const quoteData = {
        amount: selectedEstimate.quoteAmount,
        notes: selectedEstimate.quoteNotes
      };
      
      console.log('Generating PDF with data:', quoteData);
      const pdfBlob = await generateEstimatePDF(selectedEstimate, quoteData);
      console.log('PDF generated successfully, blob size:', pdfBlob.size);
      
      // Upload PDF to Firebase Storage
      const pdfPath = `signatures/${selectedEstimate.userId}/${selectedEstimate.id}/estimate_${Date.now()}.pdf`;
      console.log('Uploading PDF to path:', pdfPath);
      
      let pdfUrl;
      try {
        pdfUrl = await uploadPDFToStorage(pdfBlob, pdfPath);
        console.log('PDF uploaded successfully, URL:', pdfUrl);
        
        if (!pdfUrl) {
          throw new Error('PDF upload returned empty URL');
        }
      } catch (uploadError) {
        console.error('PDF upload failed:', uploadError);
        throw new Error(`Failed to upload PDF: ${uploadError.message}`);
      }
      
      // Create signature request ID for tracking
      const signatureRequestId = `sig_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Create signature request message in chat
      const signatureMessage = {
        id: Date.now().toString(),
        text: 'Your estimate is ready for signature. Please review the document and provide your electronic signature below.',
        sender: 'admin',
        senderName: currentUser.displayName || 'Admin',
        senderEmail: currentUser.email,
        timestamp: new Date(),
        read: false,
        isSignatureRequest: true,
        signatureRequestId: signatureRequestId,
        signaturePdfUrl: pdfUrl,
        signatureStatus: 'pending',
        estimateData: {
          id: selectedEstimate.id,
          userId: selectedEstimate.userId,
          userName: selectedEstimate.userName,
          userEmail: selectedEstimate.userEmail,
          service: selectedEstimate.service,
          description: selectedEstimate.description,
          location: selectedEstimate.location,
          address: selectedEstimate.address,
          phone: selectedEstimate.phone,
          status: selectedEstimate.status,
          createdAt: selectedEstimate.createdAt,
          quoteAmount: selectedEstimate.quoteAmount,
          quoteNotes: (() => {
            // Find the latest quote message to get the actual quote notes
            const messages = selectedEstimate.messages || [];
            const quoteMessages = messages.filter(msg => msg.isQuote && msg.quoteNotes);
            const latestQuote = quoteMessages[quoteMessages.length - 1];
            return latestQuote ? latestQuote.quoteNotes : selectedEstimate.quoteNotes || '';
          })()
        }
      };
      
      // Clean the signature message to ensure it's Firestore-compatible
      const cleanSignatureMessage = {
        ...signatureMessage,
        estimateData: {
          ...signatureMessage.estimateData,
          // Ensure dates are properly serialized
          createdAt: signatureMessage.estimateData.createdAt ? 
            (signatureMessage.estimateData.createdAt.toDate ? signatureMessage.estimateData.createdAt.toDate() : signatureMessage.estimateData.createdAt) : 
            null
        }
      };
      
      console.log('Creating signature message with PDF URL:', cleanSignatureMessage.signaturePdfUrl);
      console.log('Full signature message:', cleanSignatureMessage);

      // Remove any undefined values
      Object.keys(cleanSignatureMessage.estimateData).forEach(key => {
        if (cleanSignatureMessage.estimateData[key] === undefined) {
          delete cleanSignatureMessage.estimateData[key];
        }
      });

      const currentMessages = selectedEstimate.messages || [];
      const updatedMessages = [...currentMessages, cleanSignatureMessage];
      
      // Update estimate with signature request
      await updateDoc(doc(db, 'estimates', selectedEstimate.id), {
        messages: updatedMessages,
        signatureRequestId: signatureRequestId,
        signatureStatus: 'pending',
        signaturePdfUrl: pdfUrl,
        signatureRequestedAt: new Date(),
        updatedAt: new Date(),
        hasUnreadMessages: false // Admin sent it, so no unread flag needed
      });
      
        // Send notification to client
        try {
          await createEstimateNotification(selectedEstimate, 'signature_requested', selectedEstimate.userId);
        } catch (notificationError) {
          console.error('Error sending signature notification:', notificationError);
        }
        
      showNotification('Signature request sent successfully! The client will see it in their chat.', 'success');
        
        // Refresh the estimate data
        loadEstimates();
      
    } catch (error) {
      console.error('Error sending signature request:', error);
      showNotification('Failed to send signature request. Please try again.', 'error');
    } finally {
      setSendingSignature(false);
    }
  };

  const handleCheckSignatureStatus = async () => {
    if (!selectedEstimate?.signatureRequestId) {
      showNotification('No signature request found for this estimate.', 'error');
      return;
    }

    setCheckingSignature(true);
    
    try {
      const result = await checkSignatureStatus(
        selectedEstimate.signatureRequestId,
        selectedEstimate.id
      );
      
      if (result.success) {
        if (result.isComplete) {
          showNotification('Document has been signed!', 'success');
          // Send notification to admin
          try {
            await createEstimateNotification(selectedEstimate, 'signature_completed', selectedEstimate.userId);
          } catch (notificationError) {
            console.error('Error sending signature completion notification:', notificationError);
          }
        } else if (result.isDeclined) {
          showNotification('Document was declined by the client.', 'warning');
        } else {
          showNotification('Signature is still pending.', 'info');
        }
        
        // Refresh the estimate data
        loadEstimates();
      } else {
        throw new Error(result.error || 'Failed to check signature status');
      }
      
    } catch (error) {
      console.error('Error checking signature status:', error);
      showNotification('Failed to check signature status. Please try again.', 'error');
    } finally {
      setCheckingSignature(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'in-progress': return '#2196f3';
      case 'quoted': return '#4caf50';
      case 'completed': 
      case 'approved': return '#9c27b0';
      case 'declined': return '#f44336';
      default: return '#666';
    }
  };

  const getStatusBadge = (status) => {
    // Define colors for each status
    const getStatusColor = (status) => {
      switch (status) {
        case 'pending': return { bg: '#fff3cd', color: '#856404', border: '#ffeaa7' }; // Yellow
        case 'in-progress': return { bg: '#d1ecf1', color: '#0c5460', border: '#74c0fc' }; // Blue
        case 'quoted': return { bg: '#e2e3ff', color: '#4c63d2', border: '#a29bfe' }; // Purple
        case 'completed': 
        case 'approved': return { bg: '#d4edda', color: '#155724', border: '#00b894' }; // Green
        case 'declined': return { bg: '#f8d7da', color: '#721c24', border: '#fd79a8' }; // Red
        default: return { bg: '#f8f9fa', color: '#495057', border: '#dee2e6' }; // Gray
      }
    };

    const statusText = 
      status === 'in-progress' ? 'In Progress' :
      status === 'completed' || status === 'approved' ? 'Completed' :
      status.charAt(0).toUpperCase() + status.slice(1);
    
    const colors = getStatusColor(status);
    
    return (
      <span style={{
        backgroundColor: colors.bg,
        color: colors.color,
        border: `1px solid ${colors.border}`,
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        display: 'inline-block'
      }}>
        {statusText}
      </span>
    );
  };

  const getMessageCount = (estimate) => {
    return estimate.messages ? estimate.messages.length : 0;
  };

  return (
    <Layout>
      <div className="analytics-container admin-estimates-page" style={{ 
        padding: windowWidth < 768 ? '0.5rem' : '1rem 2rem',
        maxWidth: '100%',
        overflowX: 'hidden',
        overflowY: 'visible',
        boxSizing: 'border-box',
        height: 'auto',
        minHeight: 'auto'
      }}>
        {/* Toast Notification */}
        {notification && (
          <div style={{
            position: 'fixed',
            top: '20px',
            right: windowWidth < 768 ? '10px' : '20px',
            left: windowWidth < 768 ? '10px' : 'auto',
            zIndex: 10000,
            maxWidth: windowWidth < 768 ? 'calc(100vw - 20px)' : '400px',
            padding: windowWidth < 480 ? '0.75rem 1rem' : '1rem 1.5rem',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            backgroundColor: notification.type === 'success' ? '#4caf50' : '#f44336',
            color: 'white',
            fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            animation: 'slideInRight 0.3s ease-out',
            wordWrap: 'break-word'
          }}>
            <span style={{ fontSize: '1.2rem' }}>
              {notification.type === 'success' ? '✅' : '❌'}
            </span>
            <span style={{ flex: 1 }}>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '1.2rem',
                padding: '0',
                marginLeft: '0.5rem'
              }}
            >
              ✕
            </button>
          </div>
        )}

        <style>
          {`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}
        </style>

        {/* Header */}
        <div style={{ 
          marginBottom: windowWidth < 768 ? '1rem' : '2rem',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: windowWidth < 768 ? 'column' : 'row',
            justifyContent: 'space-between', 
            alignItems: windowWidth < 768 ? 'flex-start' : 'center',
            gap: windowWidth < 768 ? '1rem' : '0',
            maxWidth: '100%'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1 style={{ 
                fontSize: windowWidth < 480 ? '1.25rem' : windowWidth < 768 ? '1.5rem' : '2rem', 
                fontWeight: 'bold', 
                color: '#333', 
                margin: 0,
                wordBreak: 'break-word'
              }}>
                Manage Estimates
              </h1>
              <p style={{ 
                color: '#666', 
                marginTop: '0.5rem',
                fontSize: windowWidth < 768 ? '0.8rem' : '1rem',
                margin: '0.5rem 0 0 0'
              }}>
                Review and manage customer estimate requests
              </p>
            </div>
            
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/admin/messages')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: windowWidth < 768 ? '0.75rem 1rem' : '0.75rem 1.5rem',
                fontSize: windowWidth < 768 ? '0.8rem' : '0.875rem',
                whiteSpace: 'nowrap',
                width: windowWidth < 768 ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              💬 Client Messages
            </button>
          </div>
        </div>

        {/* Statistics Cards - HARDCODED ALL 5 CARDS */}
        <div className="stats-cards-container" style={{ 
          display: windowWidth <= 480 ? 'flex' : 'grid',
          flexDirection: windowWidth <= 480 ? 'column' : 'row',
          gridTemplateColumns: windowWidth <= 480 ? '1fr' : 
                               windowWidth <= 768 ? 'repeat(2, 1fr)' : 
                               windowWidth <= 1024 ? 'repeat(3, 1fr)' :
                               'repeat(5, 1fr)', 
          gap: '0.75rem',
          marginBottom: '2rem',
          padding: '1rem',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          overflowY: 'visible',
          boxSizing: 'border-box',
          minHeight: windowWidth <= 480 ? '400px' : 'auto',
          height: 'auto'
        }}>
          {/* CARD 1: Total Estimates */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Total Estimates
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.total || 0}
            </div>
          </div>

          {/* CARD 2: Pending Review */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Pending Review
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.pending || 0}
            </div>
          </div>

          {/* CARD 3: In Progress */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              In Progress
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.inProgress || 0}
            </div>
          </div>

          {/* CARD 4: Quoted */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Quoted
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.quoted || 0}
            </div>
          </div>

          {/* CARD 5: Completed */}
          <div className="metric-card-modern" style={{ 
            background: '#f8f9fa', 
            border: '1px solid #e9ecef',
            padding: '1rem',
            width: '100%',
            minHeight: '60px',
            display: 'flex',
            flexDirection: windowWidth <= 480 ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: windowWidth <= 480 ? 'space-between' : 'center',
            borderRadius: '8px',
            marginBottom: '0.75rem',
            visibility: 'visible',
            opacity: 1,
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '500' }}>
              Completed
            </div>
            <div style={{ color: 'var(--primary-500)', fontSize: '1.5rem', fontWeight: '700' }}>
              {stats.completed || 0}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          maxWidth: '100%'
        }}>
          <div style={{ 
            display: 'flex', 
            borderBottom: '1px solid #eee',
            backgroundColor: '#f8f9fa',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch'
          }}>
            {[
              { key: 'ALL', label: 'ALL', shortLabel: 'ALL', count: stats.total },
              { key: 'PENDING', label: 'PENDING', shortLabel: 'PEND', count: stats.pending },
              { key: 'IN PROGRESS', label: 'IN PROGRESS', shortLabel: 'PROG', count: stats.inProgress },
              { key: 'QUOTED', label: 'QUOTED', shortLabel: 'QUOT', count: stats.quoted },
              { key: 'COMPLETED', label: 'COMPLETED', shortLabel: 'COMP', count: stats.completed }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: windowWidth < 768 ? '0.5rem 0.75rem' : '0.6rem 0.3rem',
                  border: 'none',
                  backgroundColor: activeTab === tab.key ? '#007bff' : 'transparent',
                  color: activeTab === tab.key ? 'white' : '#666',
                  cursor: 'pointer',
                  fontSize: windowWidth < 480 ? '0.6rem' : '0.65rem',
                  fontWeight: '600',
                  borderBottom: activeTab === tab.key ? '3px solid #007bff' : '3px solid transparent',
                  whiteSpace: 'nowrap',
                  flex: windowWidth < 768 ? '0 0 auto' : '1',
                  textAlign: 'center',
                  minWidth: windowWidth < 768 ? 'auto' : '0',
                  overflow: 'hidden',
                  flexShrink: 0
                }}
              >
                {windowWidth <= 480 ? `${tab.shortLabel} (${tab.count})` : `${tab.label} (${tab.count})`}
              </button>
            ))}
          </div>

          {/* Table */}
          <div style={{ padding: windowWidth < 480 ? '0.75rem' : '1rem' }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                <p>Loading estimates...</p>
              </div>
            ) : (
              <>
                {windowWidth >= 768 ? (
                  // Desktop Table Layout
                  <div style={{
                    width: '100%',
                    minHeight: '400px'
                  }}>
                    <div style={{ 
                      minWidth: '100%',
                      overflowX: 'auto',
                      WebkitOverflowScrolling: 'touch'
                    }}>
                      {/* Table Header */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                        gap: '1rem',
                        padding: '1rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#666'
                      }}>
                        <div>Client & Project</div>
                        <div>Status</div>
                        <div>Budget/Quote</div>
                        <div>Timeline</div>
                        <div>Last Updated</div>
                        <div>Messages</div>
                      </div>

                      {/* Table Rows */}
                      {filteredEstimates.length > 0 ? (
                        filteredEstimates.map((estimate) => (
                          <div
                            key={estimate.id}
                            onClick={() => handleViewEstimate(estimate)}
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
                              gap: '1rem',
                              padding: '1rem',
                              borderBottom: '1px solid #eee',
                              alignItems: 'center',
                              fontSize: '0.875rem',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <div>
                              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                                {estimate.service}
                              </div>
                              <div style={{ color: '#666', fontSize: '0.8rem' }}>
                                {estimate.userName} • {estimate.userEmail}
                              </div>
                            </div>
                            
                            <div>
                              {getStatusBadge(estimate.status)}
                              {estimate.signatureStatus && (
                                <div style={{ 
                                  fontSize: '0.7rem', 
                                  marginTop: '0.25rem',
                                  color: estimate.signatureStatus === 'completed' ? '#28a745' : 
                                         estimate.signatureStatus === 'pending' ? '#ffc107' : '#666'
                                }}>
                                  {estimate.signatureStatus === 'completed' ? '✅ Signed' :
                                   estimate.signatureStatus === 'pending' ? '⏳ Signature Pending' :
                                   '📝 Ready for Signature'}
                                </div>
                              )}
                            </div>
                            
                            <div style={{ 
                              fontWeight: '600', 
                              color: estimate.quoteAmount ? '#4caf50' : '#f44336' 
                            }}>
                              {estimate.quoteAmount ? `$${estimate.quoteAmount.toLocaleString()}` : 'Not quoted yet'}
                            </div>
                            
                            <div style={{ color: '#666' }}>
                              On request
                            </div>
                            
                            <div style={{ color: '#666' }}>
                              {estimate.submittedDate}
                            </div>
                            
                            <div style={{ color: '#666' }}>
                              {getMessageCount(estimate)} message{getMessageCount(estimate) !== 1 ? 's' : ''}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                          <h4 style={{ marginBottom: '0.5rem', color: '#333' }}>No estimates found</h4>
                          <p>No estimates match the current filter.</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Mobile Card Layout
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '1rem',
                    minHeight: '400px'
                  }}>
                    {filteredEstimates.length > 0 ? (
                      filteredEstimates.map((estimate) => (
                        <div
                          key={estimate.id}
                          onClick={() => handleViewEstimate(estimate)}
                          style={{
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: windowWidth < 480 ? '1rem' : '1.25rem',
                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            boxSizing: 'border-box'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {/* Mobile Card Header */}
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '1rem',
                            gap: '1rem'
                          }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ 
                                fontWeight: '600', 
                                fontSize: windowWidth < 480 ? '0.9rem' : '1rem',
                                marginBottom: '0.25rem',
                                wordBreak: 'break-word',
                                color: '#333'
                              }}>
                                {estimate.service}
                              </div>
                              <div style={{ 
                                fontSize: windowWidth < 480 ? '0.75rem' : '0.8rem', 
                                color: '#666',
                                wordBreak: 'break-word'
                              }}>
                                {estimate.userName} • {estimate.userEmail}
                              </div>
                            </div>
                            <div style={{ flexShrink: 0 }}>
                              {getStatusBadge(estimate.status)}
                            </div>
                          </div>

                          {/* Mobile Card Content */}
                          <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '0.75rem',
                            fontSize: windowWidth < 480 ? '0.8rem' : '0.875rem'
                          }}>
                            {/* Quote Amount */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{ color: '#666' }}>Quote:</span>
                              <span style={{ 
                                fontWeight: '600', 
                                color: estimate.quoteAmount ? '#4caf50' : '#f44336',
                                fontSize: windowWidth < 480 ? '0.85rem' : '0.9rem'
                              }}>
                                {estimate.quoteAmount ? `$${estimate.quoteAmount.toLocaleString()}` : 'Not quoted yet'}
                              </span>
                            </div>

                            {/* Signature Status */}
                            {estimate.signatureStatus && (
                              <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center'
                              }}>
                                <span style={{ color: '#666' }}>Signature:</span>
                                <span style={{ 
                                  fontSize: windowWidth < 480 ? '0.75rem' : '0.8rem',
                                  color: estimate.signatureStatus === 'completed' ? '#28a745' : 
                                         estimate.signatureStatus === 'pending' ? '#ffc107' : '#666',
                                  fontWeight: '500'
                                }}>
                                  {estimate.signatureStatus === 'completed' ? '✅ Signed' :
                                   estimate.signatureStatus === 'pending' ? '⏳ Pending' :
                                   '📝 Ready'}
                                </span>
                              </div>
                            )}
                            
                            {/* Bottom Info */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              paddingTop: '0.75rem',
                              borderTop: '1px solid #e5e7eb',
                              fontSize: windowWidth < 480 ? '0.75rem' : '0.8rem',
                              color: '#666'
                            }}>
                              <div>
                                📅 {estimate.submittedDate}
                              </div>
                              <div>
                                💬 {getMessageCount(estimate)} message{getMessageCount(estimate) !== 1 ? 's' : ''}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
                        <h4 style={{ marginBottom: '0.5rem', color: '#333' }}>No estimates found</h4>
                        <p>No estimates match the current filter.</p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Estimate Management Modal */}
        {showModal && selectedEstimate && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: windowWidth < 768 ? 'flex-start' : 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: windowWidth < 768 ? '1rem 0.5rem' : '2rem'
            }}
            onClick={closeModal}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: windowWidth < 768 ? '8px' : '12px',
                padding: '0',
                maxWidth: windowWidth < 768 ? '100%' : '1200px',
                width: windowWidth < 768 ? '100%' : '95%',
                maxHeight: windowWidth < 768 ? 'calc(100vh - 2rem)' : '90vh',
                overflowY: 'auto',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                margin: windowWidth < 768 ? '0' : 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                padding: windowWidth < 480 ? '1rem 1.5rem' : '1.5rem 2rem',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa'
              }}>
                <h2 style={{ 
                  margin: 0, 
                  color: '#2e7d32', 
                  fontSize: windowWidth < 480 ? '1.25rem' : '1.5rem', 
                  fontWeight: '600',
                  wordBreak: 'break-word',
                  flex: 1,
                  marginRight: '1rem'
                }}>
                  Estimate Management
                </h2>
                <button 
                  onClick={closeModal}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: windowWidth < 768 ? '1.25rem' : '1.5rem',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0.5rem',
                    flexShrink: 0
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Compact Overview Bar */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: windowWidth < 768 ? '1fr' : 'auto 1fr auto',
                gap: windowWidth < 768 ? '0.5rem' : '1rem',
                alignItems: 'center',
                padding: windowWidth < 480 ? '0.75rem 1.5rem' : '1rem 2rem',
                borderBottom: '1px solid #eee',
                background: '#ffffff'
              }}>
                {/* Status Badge */}
                <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <span style={{
                    background: getStatusColor(selectedEstimate.status || 'pending'),
                    color: '#fff',
                    padding: '0.5rem 0.9rem',
                    borderRadius: '999px',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em'
                  }}>
                    {selectedEstimate.status || 'pending'}
                  </span>
                </div>

                {/* Client Info */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '0.15rem',
                  minWidth: 0
                }}>
                  <div style={{
                    fontSize: '0.95rem',
                    color: '#1f2937',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    👤 {selectedEstimate.userName || selectedEstimate.userEmail}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    ✉️ {selectedEstimate.userEmail}
                  </div>
                </div>

                {/* Service and Dates */}
                <div style={{
                  textAlign: windowWidth < 768 ? 'left' : 'right',
                  display: 'grid',
                  gap: '0.15rem'
                }}>
                  <div style={{ fontSize: '0.9rem', color: '#111827', fontWeight: 600 }}>
                    🛠️ {selectedEstimate.service}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Submitted: {selectedEstimate.submittedDate}
                  </div>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 0
              }}>
                {/* Top Section - Estimate Details */}
                <div style={{ 
                  padding: windowWidth < 480 ? '1rem' : '1.5rem',
                  borderBottom: '1px solid #eee'
                }}>
                  <div>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#111827', fontSize: '1.25rem', fontWeight: '700' }}>
                      📋 Estimate Details
                    </h3>
                    
                    {/* Details Grid */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: windowWidth < 768 ? '1fr' : 'repeat(2, 1fr)', 
                      gap: '1.25rem',
                      marginBottom: '1.5rem'
                    }}>
                    
                      {/* Client Information */}
                      <div>
                      <h4 style={{ margin: '0 0 0.75rem 0', color: '#666', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase' }}>
                        Client Information
                      </h4>
                      <div style={{ background: '#ffffff', padding: '0.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>Name:</strong> {selectedEstimate.userName}
                        </div>
                        <div>
                          <strong>Email:</strong> {selectedEstimate.userEmail}
                        </div>
                      </div>
                      </div>

                      {/* Service Info */}
                      <div>
                      <h4 style={{ margin: '0 0 0.75rem 0', color: '#666', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase' }}>
                        Service & Description
                      </h4>
                      <div style={{ background: '#ffffff', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <strong>Service:</strong> {selectedEstimate.service}
                        </div>
                        <div>
                          <strong>Description:</strong>
                          <div style={{ marginTop: '0.5rem', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                            {selectedEstimate.description}
                          </div>
                        </div>
                      </div>
                      </div>
                    </div>

                    {/* Attached Files - Full Width */}
                    <div style={{ marginBottom: '1.25rem' }}>
                      <h4 style={{ margin: '0 0 0.75rem 0', color: '#666', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase' }}>
                        Attached Files {selectedEstimate.files && selectedEstimate.files.length > 0 ? `(${selectedEstimate.files.length})` : '(0)'}
                      </h4>
                      {selectedEstimate.files && selectedEstimate.files.length > 0 ? (
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', 
                          gap: '0.5rem',
                          background: '#ffffff', 
                          padding: '0.5rem', 
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                        }}>
                          {selectedEstimate.files.map((file, index) => (
                            <div key={index} style={{
                              border: '1px solid #ddd',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              transition: 'transform 0.2s ease',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => window.open(file.url, '_blank')}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            >
                              {file.type && file.type.startsWith('image/') ? (
                                <img
                                  src={file.url}
                                  alt={file.name}
                                  style={{
                                    width: '100%',
                                    height: '100px',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : file.type && file.type.startsWith('video/') ? (
                                <video
                                  src={file.url}
                                  style={{
                                    width: '100%',
                                    height: '100px',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <div style={{
                                  height: '100px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: '#f5f5f5',
                                  fontSize: '2rem'
                                }}>
                                  📄
                                </div>
                              )}
                              <div style={{ padding: '0.5rem', fontSize: '0.75rem', textAlign: 'center', fontWeight: '500' }}>
                                {file.name.length > 15 ? file.name.substring(0, 15) + '...' : file.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          background: '#ffffff',
                          padding: '1.25rem',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          textAlign: 'center',
                          color: '#666',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                        }}>
                          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📁</div>
                          <p style={{ margin: 0, fontSize: '0.875rem' }}>No files attached</p>
                        </div>
                      )}
                    </div>

                    {/* Update Status - Full Width */}
                    <div style={{ maxWidth: windowWidth < 768 ? '100%' : '400px' }}>
                      <h4 style={{ margin: '0 0 0.75rem 0', color: '#666', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase' }}>
                        Update Status
                      </h4>
                      <div style={{ background: '#ffffff', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                        <select
                          value={statusUpdate}
                          onChange={(e) => setStatusUpdate(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            marginBottom: '1rem'
                          }}
                        >
                          <option value="pending">Pending Review</option>
                          <option value="in-progress">In Progress</option>
                          <option value="quoted">Quoted</option>
                          <option value="approved">Approved</option>
                          <option value="completed">Completed</option>
                          <option value="declined">Declined</option>
                        </select>
                        
                        <button
                          onClick={async () => {
                            setUpdatingStatus(true);
                            setStatusUpdateSuccess(false);
                            try {
                              await updateDoc(doc(db, 'estimates', selectedEstimate.id), {
                                status: statusUpdate,
                                updatedAt: new Date()
                              });
                              
                              // Send notification to client about status change
                              try {
                                const updatedEstimate = {
                                  ...selectedEstimate,
                                  status: statusUpdate
                                };
                                
                                // Determine notification type based on status
                                let notificationType = null;
                                if (statusUpdate === 'pending') {
                                  notificationType = 'estimate_status_pending';
                                } else if (statusUpdate === 'in-progress') {
                                  notificationType = 'estimate_status_in_progress';
                                } else if (statusUpdate === 'completed' || statusUpdate === 'approved') {
                                  notificationType = 'estimate_status_completed';
                                } else if (statusUpdate === 'quoted') {
                                  notificationType = 'estimate_quoted';
                                } else if (statusUpdate === 'declined') {
                                  notificationType = 'estimate_declined';
                                }
                                
                                // Send notification if we have a valid type
                                if (notificationType) {
                                  await createEstimateNotification(updatedEstimate, notificationType, selectedEstimate.userId);
                                }
                              } catch (notificationError) {
                                console.error('Error sending status change notification to client:', notificationError);
                              }
                              
                              setStatusUpdateSuccess(true);
                              // Hide success message after 3 seconds
                              setTimeout(() => setStatusUpdateSuccess(false), 3000);
                            } catch (error) {
                              console.error('Failed to update status:', error);
                            } finally {
                              setUpdatingStatus(false);
                            }
                          }}
                          style={{
                            background: updatingStatus ? '#ccc' : '#2e7d32',
                            color: 'white',
                            border: 'none',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '6px',
                            cursor: updatingStatus ? 'not-allowed' : 'pointer',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                          disabled={updatingStatus}
                        >
                          {updatingStatus && (
                            <div style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid transparent',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                          )}
                          {updatingStatus ? 'Updating...' : 'Update Status'}
                        </button>
                        
                        {/* Inline Success Message */}
                        {statusUpdateSuccess && (
                          <div style={{
                            marginTop: '12px',
                            padding: '12px 16px',
                            background: '#d4edda',
                            color: '#155724',
                            border: '1px solid #c3e6cb',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            animation: 'fadeIn 0.3s ease-in'
                          }}>
                            <span style={{ fontSize: '16px' }}>✅</span>
                            Status updated successfully!
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Section - Messages History (Full Width) */}
                <div style={{ 
                  padding: windowWidth < 480 ? '1rem' : '1.5rem',
                  background: '#f9fafb',
                  display: 'flex', 
                  flexDirection: 'column'
                }}>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#111827', fontSize: '1.25rem', fontWeight: '700' }}>
                    💬 Messages History
                  </h3>
                  
                  {/* Messages Display */}
                  <div style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    border: '1px solid #d1d5db',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '400px',
                    maxHeight: '500px',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.08)'
                  }}>
                    {/* Client Info Header */}
                    <div style={{
                      padding: '0.9rem 1rem',
                      borderBottom: '1px solid #e5e7eb',
                      background: 'linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2
                    }}>
                      <div style={{ 
                        fontSize: '0.95rem', 
                        color: '#111827', 
                        fontWeight: '600'
                      }}>
                        📋 Client: {selectedEstimate.userName || selectedEstimate.userEmail?.split('@')[0] || 'Client'}
                      </div>
                      {selectedEstimate.messages && selectedEstimate.messages.length > 0 && (
                        <div style={{
                          fontSize: '0.78rem',
                          color: '#6b7280',
                          marginTop: '0.25rem',
                          fontWeight: 500
                        }}>
                          {selectedEstimate.messages.length} message{selectedEstimate.messages.length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </div>

                    {/* Messages List */}
                    <div 
                      id="messages-container"
                      style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0.9rem',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem'
                      }}>
                      {selectedEstimate.messages && selectedEstimate.messages.length > 0 ? (
                        selectedEstimate.messages.map((message, index) => {
                          const isAdmin = message.sender === 'admin';
                          const isClient = message.sender === 'client';
                          
                          return (
                            <div
                              key={index}
                              style={{
                                display: 'flex',
                                gap: '0.75rem',
                                alignItems: 'flex-start'
                              }}
                            >
                              {/* Avatar/Icon */}
                              <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: isAdmin ? 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '1.1rem',
                                flexShrink: 0,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                {isAdmin ? '👨‍💼' : isClient ? '👤' : '🤖'}
                              </div>
                              
                              {/* Message Bubble */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  background: isAdmin ? '#e8f5e9' : '#f3f4f6',
                                  padding: '0.75rem',
                                  borderRadius: '12px',
                                  borderTopLeftRadius: '4px',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                  border: `1px solid ${isAdmin ? '#c8e6c9' : '#e5e7eb'}`
                                }}>
                                  <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '0.4rem'
                                  }}>
                                    <strong style={{
                                      color: isAdmin ? '#2e7d32' : '#4b5563',
                                      fontSize: '0.85rem',
                                      fontWeight: '600'
                                    }}>
                                      {isAdmin ? 'Admin' : isClient ? selectedEstimate.userName || 'Client' : 'System'}
                                    </strong>
                                    {message.timestamp && (
                                      <span style={{
                                        fontSize: '0.7rem',
                                        color: '#9ca3af'
                                      }}>
                                        {new Date(message.timestamp).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ 
                                    color: '#1f2937', 
                                    lineHeight: '1.5',
                                    fontSize: '0.9rem',
                                    wordWrap: 'break-word'
                                  }}>
                                    {message.isQuote ? (
                                      <div>
                                        <div style={{ fontWeight: '600', color: '#16a34a', marginBottom: '0.25rem' }}>
                                          💰 Quote: ${message.quoteAmount?.toLocaleString()}
                                        </div>
                                        {message.quoteNotes && (
                                          <div style={{ fontSize: '0.85rem', color: '#6b7280', fontStyle: 'italic' }}>
                                            {message.quoteNotes}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      message.text || 'File shared'
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div style={{
                          textAlign: 'center',
                          padding: '2rem',
                          color: '#999',
                          fontStyle: 'italic'
                        }}>
                          No messages yet
                        </div>
                      )}
                    </div>

                    {/* Message Input Area */}
                    <div style={{
                      borderTop: '1px solid #e5e7eb',
                      padding: '1rem',
                      background: '#ffffff'
                    }}>
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message here..."
                        style={{
                          width: '100%',
                          minHeight: '80px',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          marginBottom: '0.75rem'
                        }}
                        disabled={sendingMessage}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        style={{
                          background: sendingMessage || !newMessage.trim() ? '#9ca3af' : '#2e7d32',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          cursor: sendingMessage || !newMessage.trim() ? 'not-allowed' : 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          transition: 'background 0.2s'
                        }}
                      >
                        {sendingMessage ? (
                          <>
                            <div style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid transparent',
                              borderTop: '2px solid white',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            Sending...
                          </>
                        ) : (
                          '📤 Send Message'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* CSS Animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeIn {
          0% { 
            opacity: 0; 
            transform: translateY(-10px); 
          }
          100% { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
      `}</style>
    </Layout>
  );
};

export default AdminEstimates;